import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en.json';
import tr from './tr.json';
import es from './es.json';
import pt from './pt.json';
import zh from './zh.json';
import th from './th.json';
import hi from './hi.json';
import pl from './pl.json';
import ru from './ru.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'EN', name: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'ES', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'PT', name: 'Português', flag: '🇧🇷' },
  { code: 'tr', label: 'TR', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'zh', label: 'ZH', name: '中文', flag: '🇨🇳' },
  { code: 'hi', label: 'HI', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'th', label: 'TH', name: 'ไทย', flag: '🇹🇭' },
  { code: 'pl', label: 'PL', name: 'Polski', flag: '🇵🇱' },
  { code: 'ru', label: 'RU', name: 'Русский', flag: '🇷🇺' },
];

// Country code (ISO 3166-1 alpha-2) → preferred language code mapping.
// Used by geo-IP fallback when browser language doesn't resolve.
const COUNTRY_LANG = {
  // Spanish-speaking
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es',
  EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es',
  SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', PR: 'es',
  // Portuguese
  BR: 'pt', PT: 'pt', AO: 'pt', MZ: 'pt',
  // Turkish
  TR: 'tr',
  // Chinese
  CN: 'zh', TW: 'zh', HK: 'zh', SG: 'zh', MO: 'zh',
  // Hindi
  IN: 'hi',
  // Thai
  TH: 'th',
  // Polish
  PL: 'pl',
  // Russian
  RU: 'ru', BY: 'ru', KZ: 'ru', KG: 'ru', UA: 'ru',
};

// Map browser language code → our supported code (handles regional variants like pt-BR → pt)
function detectBrowserLanguage() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return null;
  const supported = SUPPORTED_LANGUAGES.map((l) => l.code);
  const candidates = [];
  if (navigator.languages && navigator.languages.length) {
    candidates.push(...navigator.languages);
  }
  if (navigator.language) candidates.push(navigator.language);

  for (const raw of candidates) {
    if (!raw) continue;
    const lower = raw.toLowerCase();
    const base = lower.split('-')[0];
    if (supported.includes(base)) return base;
    if (lower.startsWith('pt')) return 'pt';
    if (lower.startsWith('zh')) return 'zh';
  }
  return null;
}

// Async geo-IP lookup using ipapi.co (free, no key required, ~50ms).
// NOTE: This call can be blocked by CORS or ad-blockers and that's fine — we silently fall back.
async function detectGeoLanguage() {
  if (typeof window === 'undefined') return null;
  try {
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 2000);
    // Catch network/CORS errors at fetch level to keep the console clean
    const resp = await fetch('https://ipapi.co/json/', { signal: ctrl.signal, mode: 'cors' })
      .catch(() => null);
    clearTimeout(timeout);
    if (!resp || !resp.ok) return null;
    const data = await resp.json().catch(() => null);
    if (!data) return null;
    const cc = (data.country_code || data.country || '').toUpperCase();
    if (cc && COUNTRY_LANG[cc]) return COUNTRY_LANG[cc];
  } catch (_) { /* network or abort - ignore */ }
  return null;
}

function resolveInitialLanguage() {
  if (typeof window === 'undefined') return 'en';
  // 1. Respect explicit user choice — never override
  const saved = localStorage.getItem('lang');
  if (saved && SUPPORTED_LANGUAGES.some((l) => l.code === saved)) return saved;
  // 2. Auto-detect from browser
  const detected = detectBrowserLanguage() || 'en';
  try {
    localStorage.setItem('lang', detected);
    localStorage.setItem('lang_auto_detected', '1');
  } catch (_) { /* ignore */ }
  return detected;
}

const initialLang = resolveInitialLanguage();

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    tr: { translation: tr },
    es: { translation: es },
    pt: { translation: pt },
    zh: { translation: zh },
    th: { translation: th },
    hi: { translation: hi },
    pl: { translation: pl },
    ru: { translation: ru },
  },
  lng: initialLang,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

// Async geo-IP refinement — only runs if user hasn't explicitly picked a language.
// Browser-language detection is instant; geo-IP is the secondary, more-accurate signal.
if (typeof window !== 'undefined' && localStorage.getItem('lang_user_picked') !== '1') {
  setTimeout(() => {
    detectGeoLanguage().then((geoLang) => {
      if (!geoLang) return;
      if (geoLang === i18n.language) return;
      i18n.changeLanguage(geoLang);
      try {
        localStorage.setItem('lang', geoLang);
        localStorage.setItem('lang_auto_detected', '1');
      } catch (_) { /* ignore */ }
    });
  }, 100);
}

export default i18n;
